/**
 * ResultsList — THE browse canvas's result list, and the only component that
 * renders it (#8722).
 *
 * ⚠ THE DEFECT THIS CLOSES. The canvas rendered its results TWICE, along two
 * paths that each owned one half of the viewport range: the desktop side panel
 * (server-rendered, `hidden md:block`) and the mobile vaul drawer (mounted only
 * on the client, behind `useIsMobile`). Below `md` with JavaScript off — or with
 * a script that failed to load — the first was CSS-hidden and the second never
 * existed, so every card, the past-the-end statement and the pager were in the
 * served document and on nobody's screen. And because the two paths were two
 * copies of the list markup, a hydrated phone carried both (one hidden), which
 * is how every e2e locator on the hub learned to say `:visible`.
 *
 * So the list is ONE element (`data-slot="listings-results"`) built ONCE per
 * render by `ListingsSheet` and handed, as `children`, to whichever CHROME
 * hosts it: the in-flow `ResultsPane` (the served document at every viewport,
 * and every hydrated viewport at or above `md`), or the enhanced mobile drawer
 * (`MobileSheet`, a hydrated phone). The chrome owns the scroll container, the
 * header and the gestures; this owns every arm of the results state and the
 * pager. There is no second list for the two to disagree about.
 *
 * `density` is the one thing the host tells it: the drawer's narrow column
 * takes the short copy and the stacked layout, the pane takes the long copy
 * and the responsive grid. It is chrome-shaped spacing, never a different
 * list.
 *
 * ★ WHAT "ONE" DOES AND DOES NOT MEAN. Exactly one list is mounted in every
 * state — served, hydrating, hydrated, either side of `md`. When a hydrated
 * phone's drawer takes the list over, React MOVES it by remounting it under
 * the drawer (React has no reparenting): the served pane yields the list in
 * the same commit the drawer receives it, so the document never holds two.
 * (While a marker preview owns a phone's bottom edge the drawer is closed and
 * vaul unmounts its content — the one state with no list on screen, chosen by
 * the reader's own pin tap and undone by dismissing it, exactly as before.)
 * The hydration pass itself renders the served host (the viewport hook answers
 * the server's value during hydration), so the handover is an ordinary
 * post-hydration update, never a mismatch.
 */
import { ListingCard } from '@/shared/components/listings';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { getListingHref } from '@/shared/lib/listings/url';
import type { TransactionModeValue } from '@letsgethome/lib/types/listings';
import { plaintextString, type ListingId } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';
import type { ReactNode } from 'react';

import type { ListingsResults } from './ListingsSheet';
import { PastTheEndStatement } from './PastTheEndStatement';

/**
 * The list's identity for both test suites — "how many result lists does this
 * document hold?" is the single-list contract, and it is asked by this name.
 */
export const RESULTS_LIST_SLOT = 'listings-results';

/** Which chrome hosts the list — see the module note. */
export type ResultsListDensity = 'pane' | 'drawer';

/**
 * The per-host spellings, written out as LITERALS (Tailwind scans source as
 * text) and keyed by the density so a third host owes a row.
 */
const DENSITY = {
  pane: {
    stateHeight: 'h-48',
    cards: 'grid grid-cols-1 lg:grid-cols-2 gap-4',
    // Full width below `md` (the pane is the whole canvas width there), half
    // the canvas up to `lg`, a quarter beyond — the pane's own geometry.
    sizes: '(max-width: 767px) 100vw, (max-width: 1024px) 50vw, 25vw',
  },
  drawer: {
    stateHeight: 'h-32',
    cards: 'space-y-3',
    sizes: '100vw',
  },
} as const satisfies Record<
  ResultsListDensity,
  { stateHeight: string; cards: string; sizes: string }
>;

export interface ResultsListProps {
  results: ListingsResults;
  /** The map-sync affordance — see `ListingsSheetProps.onCardHighlight`. */
  onCardHighlight?: (id: ListingId | null) => void;
  /** Whether the next infinite-scroll page is on its way. */
  isLoadingMore?: boolean;
  /** Whether there is a next infinite-scroll page (the drawer's sentinel). */
  hasMore?: boolean;
  mode: TransactionModeValue;
  /** The crawlable pager — rendered after the last card in every host. */
  footer?: ReactNode;
  density: ResultsListDensity;
}

export function ResultsList({
  results,
  onCardHighlight,
  isLoadingMore,
  hasMore,
  mode,
  footer,
  density,
}: ResultsListProps) {
  const t = useTranslations('listings.browse');
  const tCommon = useTranslations('common');
  const d = DENSITY[density];

  return (
    <div data-slot={RESULTS_LIST_SLOT}>
      {results.kind === 'pastTheEnd' ? (
        <PastTheEndStatement className={d.stateHeight} />
      ) : results.kind === 'error' ? (
        <div className={cn('flex flex-col items-center justify-center text-center', d.stateHeight)}>
          <p className="text-destructive font-medium">
            {density === 'drawer' ? t('failedToLoadShort') : t('failedToLoad')}
          </p>
          <p className="text-sm text-muted-foreground mt-1">{plaintextString(results.message)}</p>
        </div>
      ) : results.kind === 'loading' ? (
        <div
          role="status"
          className={cn('flex flex-col items-center justify-center gap-2', d.stateHeight)}
        >
          <div className="h-6 w-6 border-2 border-highlight-text border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">{tCommon('loading')}</p>
        </div>
      ) : results.listings.length === 0 ? (
        <div
          className={cn('flex items-center justify-center text-muted-foreground', d.stateHeight)}
        >
          <p>{density === 'drawer' ? t('noListingsShort') : t('noListings')}</p>
        </div>
      ) : (
        <div className={d.cards}>
          {results.listings.map((listing) => (
            <ListingCard
              key={listing.id}
              listing={listing}
              // The SAME href helper the home carousels use, so the hub and
              // the carousels cannot drift on URL shape (LET-83). Nothing
              // intercepts the click — the anchor IS the behaviour, hydrated
              // or not (obs. 4). `onHighlightChange` is wired in every host: a
              // stylus hovers and a keyboard tabs, and a TOUCH tap reports
              // nothing at all (`CardSurface` owns the filter).
              href={getListingHref(listing)}
              onHighlightChange={
                onCardHighlight
                  ? (highlighted) => onCardHighlight(highlighted ? listing.id : null)
                  : undefined
              }
              transactionType={mode}
              sizes={d.sizes}
            />
          ))}
        </div>
      )}

      {/* Loading more indicator */}
      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="h-6 w-6 border-2 border-highlight-text border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Pagination sentinel for the drawer's infinite scroll. */}
      {density === 'drawer' && hasMore && <div className="h-1" aria-hidden="true" />}

      {/* The crawlable pager — the SSR contract; infinite scroll is the
          enhancement. It is inside the ONE list, so the served document
          carries its anchors at every viewport and hydration moves them with
          the list rather than adding a copy (the link-parity invariant). */}
      {footer}
    </div>
  );
}
